import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import * as THREE from "three";
import gsap from "gsap";
import { Html, useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import useSound from "use-sound";
import { OverlayCanvas } from "./OverlayCanvas";
import { BottomRightZipperSlider } from "./BottomRightZipperSlider";
import { useBuildingDetailStore } from "@/stores/buildingDetailStore";
import { EditorPresetSelector } from "@/components/Editor/EditorPresetSelector";
import { EditorBoxList } from "@/components/Editor/EditorBoxList";
import { TenantInputForm } from "@/components/TenantInputForm";
import { useAuthStore } from "@/stores/authStore";
import { UI_THEME, getTypography } from "@/config/uiTheme";
import type { BuildingStructureBox } from "@/utils/supabase";

// 에디터 박스 타입
interface EditorBox {
  id: string;
  position: [number, number, number];
  scale: [number, number, number];
}

/**
 * 3D 버튼 컴포넌트 (button.glb 사용)
 * 입주 폼 닫기 버튼으로 사용
 */
interface TenantFormButtonProps {
  position: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  onClick: () => void;
}

function TenantFormButton({
  position,
  rotation,
  onClick,
  scale,
}: TenantFormButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  const groupRef = useRef<THREE.Group>(null);

  // 클릭 사운드
  const [playClickSound] = useSound(
    import.meta.env.BASE_URL + "/sound/click.wav",
    { volume: 0.3 }
  );

  // 버튼 모델 로드
  const { scene } = useGLTF(import.meta.env.BASE_URL + "/models/button.glb");

  // 모델 클론
  const clonedScene = useMemo(() => {
    if (!scene) return null;
    const clone = scene.clone();
    clone.traverse((child) => {
      // sphere 메시에만 MeshStandardMaterial 적용
      if (
        child instanceof THREE.Mesh &&
        child.name.toLowerCase().includes("sphere")
      ) {
        child.material = new THREE.MeshStandardMaterial({
          color: "#ff6b6b",
          metalness: 0.5,
          roughness: 0.4,
        });
      }
    });
    return clone;
  }, [scene]);

  // 호버 시 색상 변경 (sphere만)
  useFrame(() => {
    if (clonedScene) {
      clonedScene.traverse((child) => {
        if (
          child instanceof THREE.Mesh &&
          child.name.toLowerCase().includes("sphere") &&
          child.material instanceof THREE.MeshStandardMaterial
        ) {
          child.material.color.set(isHovered ? "#ff8888" : "#ff6b6b");
          child.material.emissive.set(isHovered ? "#ff3333" : "#551111");
          child.material.emissiveIntensity = isHovered ? 0.5 : 0.2;
        }
      });
    }
  });

  return (
    <group
      ref={groupRef}
      position={position}
      scale={scale}
      rotation={rotation || [0, 0, 0]}
      onClick={(e) => {
        e.stopPropagation();
        playClickSound();
        onClick();
      }}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
    >
      {clonedScene ? (
        <primitive object={clonedScene} />
      ) : (
        // 폴백: 원형 버튼
        <mesh>
          <cylinderGeometry args={[0.5, 0.5, 0.2, 32]} />
          <meshStandardMaterial
            color={isHovered ? "#ff8888" : "#ff6b6b"}
            metalness={0.5}
            roughness={0.4}
          />
        </mesh>
      )}
    </group>
  );
}

// GLB 파일 프리로드
useGLTF.preload(import.meta.env.BASE_URL + "/models/button.glb");

interface BottomRightOverlayProps {
  /** 모드: 'building' = 건물 선택, 'editor' = 빈 셀 에디터 */
  mode: "building" | "editor";
  /** ZIP 다운로드 완료 콜백 (building 모드) */
  onZipComplete?: () => void;
  /** ESC 동작 콜백 (건물 상세 닫기 등) */
  onEscape?: () => void;
  /** 에디터 모드 props */
  editorProps?: {
    boxes: EditorBox[];
    setBoxes: React.Dispatch<React.SetStateAction<EditorBox[]>>;
    selectedBoxId: string | null;
    setSelectedBoxId: React.Dispatch<React.SetStateAction<string | null>>;
    onClose: () => void;
  };
  /** 건물 모드 props */
  buildingProps?: {
    buildingId: string;
    buildingStructure: BuildingStructureBox[];
    onTenantCreated?: () => void;
  };
}

/**
 * BottomRight 오버레이 컴포넌트
 * - building 모드: 건물 선택 시 지퍼 슬라이더 표시 (progress=0일 때 입주 폼 표시)
 * - editor 모드: 빈 셀 클릭 시 프리셋 선택기 + 박스 목록 표시
 */
export function BottomRightOverlay({
  mode,
  onZipComplete,
  onEscape,
  editorProps,
  buildingProps,
}: BottomRightOverlayProps) {
  const zipperProgress = useBuildingDetailStore((s) => s.zipperProgress);
  const setZipperProgress = useBuildingDetailStore((s) => s.setZipperProgress);
  const hasTriggeredDownloadRef = useRef(false);

  // 입주 폼 표시 상태 (지퍼가 거의 다 열렸을 때 + 메인 오버레이가 다 떴을 때)
  const showTenantForm = false;
  const isAnonymous = useAuthStore.getState().isAnonymous();

  // 애니메이션을 위한 ref
  const pledgeRef = useRef<THREE.Group>(null);
  const innerPledgeRef = useRef<HTMLDivElement>(null);
  const lastShowRef = useRef(showTenantForm);

  // 입주 폼 애니메이션 (사용자 요청: 50% 0% 오리진, 100vw/vh에서 등장)
  useEffect(() => {
    // 초기 상태 설정
    if (innerPledgeRef.current) {
      gsap.set(innerPledgeRef.current, {
        x: "0vw",
        y: "100vh",
        opacity: 0,
      });
    }
  }, []);

  useEffect(() => {
    if (showTenantForm !== lastShowRef.current) {
      if (showTenantForm && innerPledgeRef.current) {
        // 등장: 화면 밖(100vw, 100vh)에서 원래 위치로
        gsap.fromTo(
          innerPledgeRef.current,
          {
            x: "0vw",
            y: "100vh",
            opacity: 0,
          },
          {
            x: 0,
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: "power2.out",
          }
        );
      } else if (!showTenantForm && innerPledgeRef.current) {
        // 퇴장: 화면 밖으로 다시 숨김
        gsap.to(innerPledgeRef.current, {
          x: "0vw",
          y: "100vh",
          opacity: 0,
          duration: 0.6,
          ease: "power2.in",
        });
      }
      lastShowRef.current = showTenantForm;
    }
  }, [showTenantForm]);

  // 지퍼가 progress=0에 도달했을 때 입주 폼 표시
  const handleProgressChange = useCallback(
    (value: number) => {
      setZipperProgress(value);
      if (value < 0.5) {
        hasTriggeredDownloadRef.current = false;
      }
      if (value >= 0.95 && !hasTriggeredDownloadRef.current) {
        hasTriggeredDownloadRef.current = true;
        onZipComplete?.();
      }
    },
    [setZipperProgress, onZipComplete]
  );

  // 입주 완료 핸들러
  const handleTenantCreated = useCallback(() => {
    setZipperProgress(0.5);
    buildingProps?.onTenantCreated?.();
  }, [setZipperProgress, buildingProps]);

  return (
    <OverlayCanvas corner="bottomRight" zoom={50}>
      {/* 건물 모드: 지퍼 슬라이더 + 입주 폼 */}
      {mode === "building" && (
        <>
          <group position={[5.5, 1, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            {/* 입주 폼 (progress=0일 때 표시) */}
            {buildingProps && zipperProgress < 0.05 && (
              <group
                ref={pledgeRef}
                visible={showTenantForm || lastShowRef.current}
              >
                <Html
                  transform
                  occlude={false}
                  style={{ pointerEvents: "auto" }}
                  scale={0.6}
                  position={[0.4, 0.9, 0]}
                  rotation={[0, 0, Math.PI / 2]}
                >
                  <div
                    ref={innerPledgeRef}
                    style={{
                      willChange: "transform, opacity",
                    }}
                  >
                    {isAnonymous ? (
                      <div
                        className="py-3 rounded-lg text-center"
                        style={{
                          color: UI_THEME.text.muted,
                          ...getTypography("heading", "h1"),
                        }}
                      >
                        입주를 위해서는 로그인 필요합니다
                      </div>
                    ) : (
                      <TenantInputForm
                        buildingId={buildingProps.buildingId}
                        buildingStructure={buildingProps.buildingStructure}
                        onTenantCreated={handleTenantCreated}
                      />
                    )}
                  </div>
                </Html>
              </group>
            )}
            {/* 3D 닫기 버튼 */}
            {/* 3D ESC 버튼 - 클릭 시 건물 상세 닫기 */}
            {onEscape && (
              <TenantFormButton
                position={[0, 3.5, 3]}
                rotation={[Math.PI / 2, 0, 0]}
                scale={0.8}
                onClick={onEscape}
              />
            )}
            {/* 지퍼 슬라이더 */}
            <BottomRightZipperSlider
              progress={zipperProgress}
              onProgressChange={handleProgressChange}
              startText="ZIP 열기"
              endText="ZIP 서류 떼기"
              position={[-3, 2.7, 0]}
              textColor="#ffffff"
              zipperLength={4.5}
              enabled={true}
              scale={1.0}
            />
          </group>
        </>
      )}

      {/* 에디터 모드: 프리셋 선택기 + 박스 목록 + 3D 프리뷰 */}
      {mode === "editor" && editorProps && (
        <>
          <group position={[5.5, 1, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            {/* 3D ESC 버튼 - 클릭 시 건물 상세 닫기 */}
            {onEscape && (
              <TenantFormButton
                position={[0, 3.5, 3]}
                rotation={[Math.PI / 2, 0, 0]}
                scale={0.8}
                onClick={onEscape}
              />
            )}
            {/* HTML UI - 에디터 패널 (빈티지 문서 스타일) */}
            <Html
              position={[-0, -0, 0]}
              transform
              occlude={false}
              style={{ pointerEvents: "auto" }}
              scale={0.8}
              rotation={[0, 0, Math.PI / 2]}
            >
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  padding: "16px",
                  background:
                    "linear-gradient(180deg, #d4c4a8 0%, #c9b896 100%)",
                  borderRadius: "4px",
                  fontFamily:
                    "'Pretendard GOV Variable', 'Noto Serif KR', 'Georgia', serif",
                  color: "#2a2a2a",
                  boxShadow: `
                  0 4px 8px rgba(0,0,0,0.3),
                  inset 0 1px 0 rgba(255,255,255,0.3),
                  inset 0 -2px 0 rgba(0,0,0,0.1)
                `,
                  border: "2px solid #8b7355",
                }}
              >
                {/* 왼쪽: 기본형 선택 + 저장 */}
                <div
                  style={{
                    minWidth: "160px",
                    paddingRight: "12px",
                    borderRight: "1px solid #c4b9a0",
                  }}
                >
                  <EditorPresetSelector
                    boxes={editorProps.boxes}
                    setBoxes={editorProps.setBoxes}
                    setSelectedBoxId={editorProps.setSelectedBoxId}
                  />
                </div>

                {/* 오른쪽: 구조물 목록 + 편집 */}
                <div
                  style={{
                    minWidth: "220px",
                    maxHeight: "280px",
                    overflowY: "auto",
                  }}
                >
                  <EditorBoxList
                    boxes={editorProps.boxes}
                    setBoxes={editorProps.setBoxes}
                    selectedBoxId={editorProps.selectedBoxId}
                    setSelectedBoxId={editorProps.setSelectedBoxId}
                  />
                </div>
              </div>
            </Html>
          </group>
        </>
      )}
    </OverlayCanvas>
  );
}

export default BottomRightOverlay;
